import Grid from '@mui/material/Grid';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';
import { Form, Formik } from 'formik';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Box, useMediaQuery } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import * as Yup from 'yup';
import { useLocation, useNavigate } from 'react-router-dom';
import { usePostHog } from 'posthog-js/react';
import { AccountData, Language } from '../api';
import { useLanguage } from '../utils/GeneralUtils';
import { PassService } from '../api/services/PassService';
import { LanguageSwitcher } from '../shared/ui/LanguageSwitcher';
import { AccountLogo } from '../shared/ui/AccountLogo';
import { useManagePasses } from './ManagePassesContext';
import RotatingLoader from '../ui/RotatingLoader';
import { StudioManagedMembership } from './components/StudioManagedMembership';

export const PassLogin = () => {
  const { t } = useTranslation();
  const isSmallScreen = useMediaQuery('(max-width:600px)');
  const navigate = useNavigate();
  const { login, logout, isLoggedIn, account } = useManagePasses();
  const posthog = usePostHog();
  const location = useLocation();
  const language = useLanguage();
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(false);
  const [codeLookupLoading, setCodeLookupLoading] = useState(false);
  // A lookup only resolves off a real pass, so a 200 carrying no passes means every pass this
  // customer holds is a fixed-schedule membership -- the server hides those. There is no dashboard
  // to send them to; show them who books their sessions instead.
  const [studioManagedAccount, setStudioManagedAccount] = useState<AccountData | null>(null);

  // A ?code= link names one specific pass, so it has to beat any session cached
  // in localStorage -- a forwarded link, or a member holding passes at two
  // studios, would otherwise land silently on the previously stored dashboard.
  // While one is present this effect stands down and the lookup below navigates.
  const deepLinkCode = useMemo(() => new URLSearchParams(location.search).get('code'), [location.search]);

  useEffect(() => {
    if (deepLinkCode) return;
    if (isLoggedIn) {
      navigate('/passes/dashboard', { replace: true });
    }
  }, [isLoggedIn, deepLinkCode]);

  useEffect(() => {
    if (!deepLinkCode) return;
    void (async () => {
      // Drop whatever session was restored before resolving the link, so a
      // failed lookup falls back to a clean login form rather than someone
      // else's dashboard.
      logout();
      setCodeLookupLoading(true);
      try {
        const response = await PassService.customerPassLookupByCode(deepLinkCode);
        if (response.passes.length === 0) {
          setStudioManagedAccount(response.account);
          setCodeLookupLoading(false);
          return;
        }
        if (login(response.passes, response.orders, response.account)) {
          navigate('/passes/dashboard', { replace: true });
        } else {
          setError(true);
          setCodeLookupLoading(false);
        }
      } catch (e) {
        setError(true);
        setCodeLookupLoading(false);
      }
    })();
  }, [deepLinkCode]);

  const handleSubmit = async (values: { orderNumber: string; email: string }) => {
    setLoading(true);
    setError(false);
    try {
      const response = await PassService.customerPassLookup({
        pass_order_number: Number(values.orderNumber),
        email: values.email,
      });
      if (response.passes.length === 0) {
        setStudioManagedAccount(response.account);
      } else if (!login(response.passes, response.orders, response.account)) {
        setError(true);
      }
    } catch (e: any) {
      setError(true);
    }
    setLoading(false);
  };

  const validationSchema = Yup.object({
    // Digits only -- anything else turns into NaN below and reaches the API as a null order number.
    orderNumber: Yup.string()
      .required(t('manage_passes.login.orderNumberRequired'))
      .matches(/^\d+$/, t('manage_passes.login.orderNumberInvalid')),
    email: Yup.string().required(t('manage_passes.login.emailRequired')).email(t('manage_passes.login.invalidEmail')),
  });

  if (studioManagedAccount) {
    return <StudioManagedMembership account={studioManagedAccount} />;
  }

  if (codeLookupLoading) {
    return (
      <Grid container justifyContent="center" alignItems="center" sx={{ minHeight: '90dvh' }}>
        <RotatingLoader />
      </Grid>
    );
  }

  return (
    <Formik
      enableReinitialize
      initialValues={{ orderNumber: '', email: '' }}
      validationSchema={validationSchema}
      validateOnChange={false}
      validateOnBlur={false}
      onSubmit={handleSubmit}>
      {({ errors, values, handleChange, handleBlur }) => (
        <Form style={{ height: '100%', width: '100%' }}>
          <Grid
            container
            flexDirection="column"
            gap={isSmallScreen ? 2 : 4}
            alignItems="center"
            minHeight={isSmallScreen ? '90dvh' : '430px'}
            justifyContent={isSmallScreen ? 'flex-start' : 'center'}
            pt={isSmallScreen ? 2 : 0}
            px={isSmallScreen ? 0 : 2}>
            {/* Language switch — always on the left */}
            <Grid container justifyContent="flex-start">
              <LanguageSwitcher languages={[Language.EN, Language.HE]} />
            </Grid>

            {/* Logo */}
            <Grid container justifyContent="center" mt={isSmallScreen ? 2 : 0}>
              {account ? (
                <Box display="flex" flexDirection="column" alignItems="center" gap={1}>
                  <AccountLogo
                    account={account}
                    width={200}
                    style={{ maxWidth: isSmallScreen ? '200px' : '180px', maxHeight: '80px', objectFit: 'contain' }}
                  />
                  <Typography variant="h6" textAlign="center">
                    {account.label?.[language as 'en' | 'he'] || account.name}
                  </Typography>
                </Box>
              ) : (
                <img
                  alt="powered by Monkeybook"
                  className="logo"
                  src={'/images/logos/monkeybook-light.png'}
                  width={isSmallScreen ? '280px' : '250px'}
                />
              )}
            </Grid>

            {/* Form fields */}
            <Grid container flexDirection="column" alignItems="center" gap={1.5} mt={isSmallScreen ? 2 : 0}>
              <Grid item width="100%">
                <TextField
                  value={values.orderNumber}
                  onBlur={handleBlur}
                  type="text"
                  name="orderNumber"
                  size={isSmallScreen ? 'medium' : 'small'}
                  fullWidth
                  variant="outlined"
                  label={t('manage_passes.login.orderNumber')}
                  error={!!errors.orderNumber}
                  helperText={errors.orderNumber}
                  onChange={handleChange}
                />
              </Grid>
              <Grid item width="100%">
                <TextField
                  value={values.email}
                  type="email"
                  onBlur={handleBlur}
                  name="email"
                  size={isSmallScreen ? 'medium' : 'small'}
                  fullWidth
                  variant="outlined"
                  label={t('manage_passes.login.email')}
                  error={!!errors.email}
                  helperText={errors.email}
                  onChange={handleChange}
                />
              </Grid>
            </Grid>

            <Grid container flexDirection="column" alignItems="center" gap={1}>
              {error && (
                <Typography variant="body2" color="error" textAlign="center">
                  {t('manage_passes.login.notFound')}
                </Typography>
              )}
              <LoadingButton
                type="submit"
                loading={loading}
                variant="contained"
                fullWidth={isSmallScreen}
                sx={{ minHeight: isSmallScreen ? 48 : 40 }}>
                {t('manage_passes.login.submit')}
              </LoadingButton>
            </Grid>

            {account && (
              <Grid container justifyContent="center" alignItems="center" gap={0.5} mt="auto" pb={1} dir="ltr">
                <Typography variant="caption" color="text.secondary">
                  Powered by
                </Typography>
                <a
                  href={language === 'he' ? 'https://www.monkeybook.io/he' : 'https://www.monkeybook.io/'}
                  target="_blank"
                  rel="noreferrer"
                  onClick={() =>
                    posthog.capture('Powered By clicked', { accountId: account.id, url: location.pathname, language })
                  }>
                  <img
                    alt="powered by Monkeybook"
                    src="/images/logos/monkeybook-light.png"
                    style={{ height: '16px', display: 'block' }}
                  />
                </a>
              </Grid>
            )}
          </Grid>
        </Form>
      )}
    </Formik>
  );
};
